import { Injectable } from '@angular/core';
import { PDFDocument, rgb, StandardFonts, degrees, pushGraphicsState,translate,rotateRadians,popGraphicsState } from 'pdf-lib';

@Injectable({
  providedIn: 'root',
})
export class PdfLibService {
  constructor() {}

  /**
   * Ajoute un filigrane texte à chaque page d'un PDF encodé en base64.
   * @param base64Input Base64 brut ou data URI complet
   * @param watermarkText Texte du filigrane (ex. : "Accepté par Dr X le 01/01/2025")
   * @returns Un string base64 au format data URI prêt à afficher ou télécharger
   */

  async addWatermarkSecu(base64Input: string, watermarkTemplate: string): Promise<string> {
    try {
      const base64 = base64Input.includes(',') ? base64Input.split(',')[1] : base64Input;
      const byteArray = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const pdfDoc = await PDFDocument.load(byteArray);
      const font = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
      const pages = pdfDoc.getPages();

      const margin = 40;
      const fontSize = 10;
      const lineHeight = 14;
      const rotationDegrees = 5;
      const rotationRadians = (rotationDegrees * Math.PI) / 180;

      const lines = watermarkTemplate
        .split(';')
        .map(line => line.trim());

      const textWidths = lines.map(line => font.widthOfTextAtSize(line, fontSize));
      const boxWidth = Math.max(...textWidths) + 20;
      const boxHeight = lines.length * lineHeight + 10;

      for (const page of pages) {
        const { width } = page.getSize();
        const x = width - margin - boxWidth;
        const y = margin - 10;
        const centerX = x + boxWidth / 2;
        const centerY = y + boxHeight / 2;

        page.pushOperators(
          pushGraphicsState(),
          translate(centerX, centerY),
          rotateRadians(rotationRadians),
          translate(-centerX, -centerY)
        );

        page.drawRectangle({
          x,
          y,
          width: boxWidth,
          height: boxHeight,
          borderColor: rgb(1, 0, 0),
          borderWidth: 1.5,
          opacity: 0.2,
        });

        for (let i = 0; i < lines.length; i++) {
          page.drawText(lines[i], {
            x: x + 10,
            y: y + boxHeight - (i + 1) * lineHeight,
            size: fontSize,
            font,
            color: rgb(1, 0, 0),
            opacity: 0.4,
          });
        }

        page.pushOperators(popGraphicsState());
      }

      // 🔐 Générer un hash basé sur le contenu d’origine et le watermark fourni
      const hashSource = base64 + '|' + watermarkTemplate;
      const integrityHash = this.simpleHash(hashSource).slice(0, 12);

      // 👉 Ajouter le hash visuellement (sur pdfDoc modifié directement)
      const fontFinal = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const pagesFinal = pdfDoc.getPages();

      for (const page of pagesFinal) {
        const { width } = page.getSize();
        const hashText = `Code d'intégrité du document : ${integrityHash}`;
        const hashTextWidth = fontFinal.widthOfTextAtSize(hashText, 8);

        page.drawText(hashText, {
          x: width - margin - hashTextWidth,
          y: 20,
          size: 8,
          font: fontFinal,
          color: rgb(1, 0, 0),
        });
      }

      const finalBytes = await pdfDoc.save();
      const finalBase64 = await this.arrayBufferToBase64(finalBytes);
      return `data:application/pdf;base64,${finalBase64}`;
    } catch (error) {
      console.error('Erreur dans l’ajout du watermark avec hash', error);
      throw error;
    }
  }

  /**
   * Convertit un Uint8Array en base64 de manière fiable via FileReader
   */
  private arrayBufferToBase64(buffer: Uint8Array): Promise<string> {
    return new Promise((resolve) => {
      // Convertir Uint8Array → ArrayBuffer
      const arrayBuffer = buffer.buffer.slice(
        buffer.byteOffset,
        buffer.byteOffset + buffer.byteLength
      );

      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const reader = new FileReader();

      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1];
        resolve(base64);
      };

      reader.readAsDataURL(blob);
    });
  }

  private simpleHash(input: string | Uint8Array): string {
    const buffer = typeof input === 'string'
      ? new TextEncoder().encode(input)
      : input;

    let hash = 0;
    for (let i = 0; i < buffer.length; i++) {
      hash = (hash << 5) - hash + buffer[i];
      hash |= 0;
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * 
   * @param pdf 
   * @returns un pdf avec un copyright en haut de la page à droite
   */
  async addCopyRigthToPdf( pdf: string) {
    try{
      const pdfDoc = await PDFDocument.load(pdf)
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const actualYear = new Date().getFullYear();
      const pages = pdfDoc.getPages()
      for (let page of pages){
        const { width, height} = page.getSize();
        page.drawText(`© Hypermed Online ${actualYear}`, {
          x: width - 150,
          y: height - 15,
          size: 11,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        })
      } 
    
      const pdfBytes = await pdfDoc.save();
      const pdfBase64 = await this.arrayBufferToBase64(pdfBytes) 
      return `data:application/pdf;base64,${pdfBase64}`;
    }catch(error){
      console.error("erreur lors de l'ajout du copyright sur le pdf", error)
    }
    
  }
}
